// API Configuration - Centralized API base URL
// Production-safe: always point to the backend domain unless explicitly overridden via env
import axios from 'axios';

const isBrowser = typeof window !== 'undefined';
const hostname = isBrowser ? window.location.hostname : '';
const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.') || hostname.startsWith('10.');

// CRITICAL: Local dev uses HTTP, Production MUST use HTTPS
const defaultApiBase = isLocalhost && isBrowser
  ? `http://${hostname}:5000` // Use dynamic hostname for local testing
  : 'https://api.evenza.space'; // Production HTTPS only

const envApi = (process.env.REACT_APP_API_URL || '').trim();

// CRITICAL: Force HTTPS in production (prevent mixed content)
let API_BASE_URL = envApi || defaultApiBase;
if (!isLocalhost && API_BASE_URL.startsWith('http://')) {
  // PRODUCTION GUARD: Fail fast if HTTP detected in production build
  if (process.env.NODE_ENV === 'production') {
    throw new Error(`FATAL: HTTP API URL detected in production build: ${API_BASE_URL}`);
  }
  API_BASE_URL = API_BASE_URL.replace('http://', 'https://');
  console.error('⚠️ SECURITY WARNING: Forcing HTTPS for production API calls');
}

export { API_BASE_URL };

/**
 * AUTH-CRITICAL: Single axios instance with token in EVERY request
 * - Always includes Authorization header if token in localStorage
 * - Handles 401 gracefully (logout and stop retrying)
 * - Includes credentials for CORS
 */
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 15000,
});

// Axios interceptor: Add token to every request
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Axios interceptor: Handle 401 responses (logout user)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const requestUrl = error.config?.url || '';
    
    // Check if the failed request was an authentication request
    const isAuthRequest = 
      requestUrl.includes('/clients/login') || 
      requestUrl.includes('/clients/google-login') || 
      requestUrl.includes('/clients/register');

    // 401 = Unauthorized (token invalid/expired)
    if (status === 401 && !isAuthRequest) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Fire modal instead of navigating to /login page
      window.dispatchEvent(new CustomEvent('openLoginModal'));
    }
    
    return Promise.reject(error);
  }
);

export const API_ENDPOINTS = {
  // CSRF
  CSRF_TOKEN: `${API_BASE_URL}/api/csrf-token`,

  // Auth
  LOGIN: `${API_BASE_URL}/api/clients/login`,
  REGISTER: `${API_BASE_URL}/api/clients/register`,
  GOOGLE_LOGIN: `${API_BASE_URL}/api/clients/google-login`,
  
  // Quotations
  QUOTATIONS: `${API_BASE_URL}/api/quotations`,
  GENERATE_QUOTATION: `${API_BASE_URL}/api/quotations/generate-detailed`,
  BOOK_REQUEST: `${API_BASE_URL}/api/quotations/book-request`,
  NEGOTIATE_REQUEST: `${API_BASE_URL}/api/quotations/negotiate-request`,
  QUOTATION_MESSAGES: (id) => `${API_BASE_URL}/api/quotations/${id}/messages`,
  QUOTATION_UPDATE_REQUEST: (id) => `${API_BASE_URL}/api/quotations/${id}/update-request`,
  QUOTATION_WORK_TRACKING: (id) => `${API_BASE_URL}/api/quotations/${id}/work-tracking`,
  QUOTATION_DOWNLOAD: (id) => `${API_BASE_URL}/api/quotations/${id}/download`,
  
  // Quotation Items
  QUOTATION_ITEMS: `${API_BASE_URL}/api/quotation-items`,
  QUOTATION_MASTER: `${API_BASE_URL}/api/quotation-items/master`,
  
  // Admin
  ADMIN_DASHBOARD: `${API_BASE_URL}/api/admin/dashboard`,
  ADMIN_CONTACTS: `${API_BASE_URL}/api/admin/contacts`,
  ADMIN_USERS: `${API_BASE_URL}/api/admin/users`,
  ADMIN_LOGS: `${API_BASE_URL}/api/admin/logs`,
  ADMIN_QUOTATIONS: `${API_BASE_URL}/api/quotations/admin/quotations`,
  ADMIN_BOOKINGS: `${API_BASE_URL}/api/quotations/admin/bookings`,
  CLIENT_DASHBOARD: (email) => `${API_BASE_URL}/api/clients/dashboard/${email}`,
  CONTENT_UPLOAD: `${API_BASE_URL}/api/content/upload`,
  CONTENT_UPCOMING_EVENTS: `${API_BASE_URL}/api/content/upcoming-events`,
  CONTENT_SHOWCASE: `${API_BASE_URL}/api/content/showcase`,
  CONTENT_SHOWCASE_HIGHLIGHTS: `${API_BASE_URL}/api/content/showcase/highlights`,
  QUOTATION_PARTNERS: `${API_BASE_URL}/api/quotations/partners`,
  
  // Content
  CONTENT_HOME: `${API_BASE_URL}/api/content/home`,
  CONTENT_UPLOAD: `${API_BASE_URL}/api/content/upload`,
  CONTENT_UPCOMING_EVENTS: `${API_BASE_URL}/api/content/upcoming-events`,
  CONTENT_SHOWCASE: `${API_BASE_URL}/api/content/showcase`,
  CONTENT_SHOWCASE_HIGHLIGHTS: `${API_BASE_URL}/api/content/showcase/highlights`,
  
  // Settings
  SETTINGS: `${API_BASE_URL}/api/settings`,
  
  // Service Pages
  SERVICE_PAGES: `${API_BASE_URL}/api/service-pages`,
  SERVICE_PAGES_RECYCLE_BIN: `${API_BASE_URL}/api/service-pages/recycle-bin`,
  
  // Events
  EVENTS: `${API_BASE_URL}/api/events`,
  EVENTS_CALENDAR: `${API_BASE_URL}/api/events/calendar`,
  
  // Service Areas
  SERVICE_AREAS: `${API_BASE_URL}/api/service-areas`,
  
  // Chatbot
  CHATBOT: `${API_BASE_URL}/api/chatbot/chat`,
  
  // Analytics
  ANALYTICS_TRACK: `${API_BASE_URL}/api/analytics/track`,
  
  // Health Check
  HEALTH: `${API_BASE_URL}/api/health`,
};

export default API_BASE_URL;
